import React, { useState } from 'react'
import { X } from 'lucide-react'
import { collectionsApi } from '../../services/collectionsApi'
import { api } from '../../api/client'
import type { CollectionWithItems, CollectionItem } from '../../types/collections'
import { collectionItemNoun, getVisibleFieldNames } from '../../utils/collectionUtils'
import PictureFieldEditor from './PictureFieldEditor'

interface EditCollectionItemModalProps {
  collection: CollectionWithItems
  item: CollectionItem
  onClose: () => void
  onSuccess: () => void
}

const EditCollectionItemModal: React.FC<EditCollectionItemModalProps> = ({
  collection,
  item,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(item.data)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadingFields, setUploadingFields] = useState<Record<string, boolean>>({})

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData({
      ...formData,
      [fieldName]: value,
    })
  }

  const handleImageUpload = async (fieldName: string, file: File) => {
    setUploadingFields({ ...uploadingFields, [fieldName]: true })
    
    try {
      const uploadFormData = new FormData()
      uploadFormData.append('field_name', fieldName)
      uploadFormData.append('image', file)

      console.log('Uploading image:', { fieldName, fileSize: file.size, fileType: file.type, itemId: item.id })

      const response = await api.post(`/api/collections/${collection.id}/items/${item.id}/upload-image`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      console.log('Upload result:', response.data)
      handleFieldChange(fieldName, response.data.url)
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to upload image for ${fieldName}: ${errorMessage}`)
      console.error('Image upload error:', err)
    } finally {
      setUploadingFields({ ...uploadingFields, [fieldName]: false })
    }
  }

  // Same filtering as Create. For Edit we additionally preserve any hidden
  // values that already exist on the item (e.g. google_place_id, source on
  // a Google-resolved Place) so editing the user-facing fields doesn't drop
  // them.
  const visibleFieldSet = new Set(getVisibleFieldNames(collection))
  const visibleFieldEntries = Object.entries(collection.config.fields).filter(
    ([name]) => visibleFieldSet.has(name)
  )

  // "Person" / "Place" / "Item" per the collection being edited.
  const noun = collectionItemNoun(collection)
  const nounTitle = noun.charAt(0).toUpperCase() + noun.slice(1)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate required fields — only those we actually surfaced.
    const missingFields = visibleFieldEntries
      .filter(([fieldName, field]) => field.required && !formData[fieldName])
      .map(([fieldName]) => fieldName)

    if (missingFields.length > 0) {
      setError(`Required fields: ${missingFields.join(', ')}`)
      return
    }

    // Start from the existing item to preserve hidden fields, then overlay
    // the form's edits to the visible ones.
    const processedData: Record<string, any> = {
      ...item.data,
      schema_version: formData.schema_version || 1,
    }

    visibleFieldEntries.forEach(([fieldName, field]) => {
      const value = formData[fieldName]

      if (value !== undefined && value !== '') {
        switch (field.type) {
          case 'int':
            processedData[fieldName] = parseInt(value, 10)
            break
          case 'decimal':
            processedData[fieldName] = parseFloat(value)
            break
          case 'boolean':
            processedData[fieldName] = Boolean(value)
            break
          default:
            processedData[fieldName] = value
        }
      } else if (!field.required) {
        // For optional fields, set null instead of empty string
        processedData[fieldName] = null
      }
    })

    setIsSubmitting(true)

    try {
      await collectionsApi.items.update(collection.id, item.id, { data: processedData })
      onSuccess()
    } catch (err) {
      setError('Failed to update item')
      console.error('Error updating item:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderFieldInput = (fieldName: string, field: any) => {
    const value = formData[fieldName] || ''

    switch (field.type) {
      case 'string':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
            required={field.required}
          />
        )

      case 'int':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
            required={field.required}
          />
        )

      case 'decimal':
        return (
          <input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
            required={field.required}
          />
        )

      case 'boolean':
        return (
          <select
            value={value === true ? 'true' : value === false ? 'false' : ''}
            onChange={(e) => handleFieldChange(fieldName, e.target.value === 'true')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
            required={field.required}
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        )

      case 'url':
        return (
          <input
            type="url"
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
            placeholder="https://..."
            required={field.required}
          />
        )

      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
            placeholder="user@example.com"
            required={field.required}
          />
        )

      case 'picture':
        return (
          <PictureFieldEditor
            fieldName={fieldName}
            value={value}
            onUrlChange={(url) => handleFieldChange(fieldName, url)}
            uploading={!!uploadingFields[fieldName]}
            onCroppedFile={(file) => handleImageUpload(fieldName, file)}
            onRemove={() => handleFieldChange(fieldName, '')}
          />
        )

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-action"
            required={field.required}
          />
        )
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Edit {nounTitle}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {error && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg">
                {error}
              </div>
            )}

            {visibleFieldEntries
              .sort(([, a], [, b]) => (a.position ?? 0) - (b.position ?? 0))
              .map(([fieldName, field]) => (
              <div key={fieldName}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {fieldName}
                  {field.required && <span className="text-rose-500 ml-1">*</span>}
                </label>
                {field.description && (
                  <p className="text-sm text-gray-500 mb-2">{field.description}</p>
                )}
                {renderFieldInput(fieldName, field)}
              </div>
            ))}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 shrink-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-action text-plum rounded-lg hover:bg-action-dark transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Updating...' : `Update ${nounTitle}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditCollectionItemModal